import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

// Import pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Content from './pages/Content';
import ContentPublishing from './pages/ContentPublishing';
import PublishingTasks from './pages/PublishingTasks';
import Devices from './pages/Devices';
import DeviceMedia from './pages/DeviceMedia';
import Licenses from './pages/Licenses';
import AccountSettings from './pages/AccountSettings';
import Player from './pages/Player';

// Import admin pages
import Companies from './pages/admin/Companies';
import Users from './pages/admin/Users';
import AdminLicenses from './pages/admin/AdminLicenses';
import AdminSettings from './pages/admin/AdminSettings';

// Import CSS
import './index.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WebSocketProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/player" element={<Player />} />
            
            {/* Protected Client Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/content"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Content />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/content-publishing"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContentPublishing />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/publishing-tasks"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PublishingTasks />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/devices"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Devices />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/device-media"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DeviceMedia />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/licenses"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Licenses />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/account-settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AccountSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Protected Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute adminOnly>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/companies"
              element={
                <ProtectedRoute adminOnly>
                  <Layout>
                    <Companies />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute adminOnly>
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/licenses"
              element={
                <ProtectedRoute adminOnly>
                  <Layout>
                    <AdminLicenses />
                  </Layout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute adminOnly>
                  <Layout>
                    <AdminSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* 404 */}
            <Route path="*" element={<div>Page not found</div>} />
          </Routes>
        </Router>
        </WebSocketProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
